// Live Show Conductor (§21–29). Pure: builds DJSEMLiveConductorState from the normalized
// LivePlaybackState + an approved package. Resolves the active show cue from a
// latency-corrected position, enforces approval, handles loop/pause, and ALWAYS produces
// a defined visual state — entering safe mode rather than leaving output undefined.

import type {
  LivePlaybackState, DJSEMLiveConductorState, LiveConductorOptions, DJSEMOutputFrame,
  FallbackVisualMode, LiveConductorMode,
} from "./types";

type Pkg = Record<string, unknown>;
type Cue = Record<string, unknown>;

const COLOUR_PALETTE: Record<string, string[]> = {
  bass_pressure_red: ["#3a0a0f", "#c0263a", "#ff5a6a"], dark_violet_pressure: ["#160b2a", "#6a3fb0", "#b08bff"],
  chaos_strobe_white: ["#101014", "#f2f2f2", "#ffffff"], victory_gold: ["#2a1e05", "#e7b53c", "#fff0b0"],
  high_brightness_cyan: ["#062626", "#34e0e0", "#b0ffff"], balanced_rgb: ["#14161f", "#9aa0c0", "#d7dde7"],
  appetite_heat: ["#2a1205", "#e8632a", "#ffb070"], water_blue: ["#06203a", "#2f7fd0", "#a0d0ff"],
  danger_red_white: ["#2a0808", "#e84a4a", "#ffd0d0"], afterglow_amber: ["#241805", "#d39a52", "#ffe0a0"],
  confidence_diamond_blue: ["#081a2a", "#4aa3e8", "#bfe4ff"], street_shadow: ["#0c0e12", "#3a3f4a", "#7c8aa0"],
};
const FALLBACK_PALETTE = ["#0c0e12", "#22262e", "#3a3f4a"];

function timeline(pkg: Pkg | null): Cue[] {
  const story = pkg?.storyEngine as { showTimeline?: unknown[] } | undefined;
  return Array.isArray(story?.showTimeline) ? (story!.showTimeline as Cue[]) : [];
}

/**
 * Resolves the active show cue for a given track position. Tries an exact
 * start/end-time window match first; if none contains the position, falls back to the
 * nearest cue by start time (held), with reduced confidence.
 * @param cues - the show timeline's cues, in any order.
 * @param pos - current (latency-corrected) track position in seconds.
 * @returns the resolved cue (or null if `cues` is empty), its index, and a 0..1 confidence.
 */
export function resolveActiveCue(cues: Cue[], pos: number): { cue: Cue | null; index: number; confidence: number } {
  if (cues.length === 0) return { cue: null, index: -1, confidence: 0 };
  for (let i = 0; i < cues.length; i++) {
    const s = Number(cues[i].startTime), e = Number(cues[i].endTime);
    if (pos >= s && pos < e) return { cue: cues[i], index: i, confidence: Number(cues[i].confidence ?? 0.6) };
  }
  // nearest fallback (held cue) with reduced confidence
  let best = 0, bestD = Infinity;
  for (let i = 0; i < cues.length; i++) { const s = Number(cues[i].startTime); const d = Math.abs(s - pos); if (d < bestD) { bestD = d; best = i; } }
  return { cue: cues[best], index: best, confidence: 0.3 };
}

function fallbackMode(pkg: Pkg | null): FallbackVisualMode {
  const lr = pkg?.liveReadiness as { safeFallbackMode?: string } | undefined;
  const m = lr?.safeFallbackMode;
  return (m === "blackout" || m === "manual_hold" || m === "low_intensity_pulse" || m === "ambient_idle") ? m : "ambient_idle";
}

function safeFrame(now: number, mode: FallbackVisualMode, reason: string, pkgId?: string): DJSEMOutputFrame {
  const intensity = mode === "blackout" ? 0 : mode === "low_intensity_pulse" ? 0.18 : mode === "manual_hold" ? 0.1 : 0.12;
  return {
    timestamp: now, packageId: pkgId, cueId: undefined,
    ledWall: { mode: mode === "blackout" ? "off" : "ambient_idle", palette: FALLBACK_PALETTE, intensity, motion: "slow_drift", safe: true },
    lasers: { mode: "off", intensity: 0, triggers: [], safe: true },
    projectors: { mode: mode === "blackout" ? "off" : "idle", emojiSequence: [], density: 0, intensity: intensity * 0.5, safe: true },
    intensityBudget: { total: intensity, ledWall: intensity, lasers: 0, projectors: intensity * 0.5, dominantLayer: "ledWall" },
    safety: { safeModeActive: true, fallbackVisualMode: mode, reason },
  };
}

function liveFrame(now: number, pkgId: string, cue: Cue, intensity: number): DJSEMOutputFrame {
  const tax = (cue.taxonomyState as Record<string, unknown>) ?? {};
  const colour = String(cue.colourStory ?? tax.colourClass ?? "balanced_rgb");
  const palette = COLOUR_PALETTE[colour] ?? FALLBACK_PALETTE;
  const motion = String(tax.motionClass ?? "controlled_groove");
  const emoji = Array.isArray(cue.audienceMessage) ? (cue.audienceMessage as unknown[]).map(String) : (Array.isArray(cue.projectorMessage) ? (cue.projectorMessage as unknown[]).map(String) : []);
  const led = intensity, laser = Math.max(0, intensity - 0.15), proj = Math.max(0, intensity - 0.1);
  const dominant = led >= laser && led >= proj ? "ledWall" : (laser >= proj ? "lasers" : "projectors");
  return {
    timestamp: now, packageId: pkgId, cueId: String(cue.id ?? ""),
    ledWall: { mode: colour, palette, intensity: led, motion, safe: false },
    lasers: { mode: motion, intensity: laser, triggers: intensity > 0.7 ? ["impact"] : [], safe: false },
    projectors: { mode: String(cue.narrativeRole ?? "groove"), emojiSequence: emoji, density: Math.min(1, intensity + 0.1), intensity: proj, safe: false },
    intensityBudget: { total: intensity, ledWall: led, lasers: laser, projectors: proj, dominantLayer: dominant },
    safety: { safeModeActive: false },
  };
}

/**
 * Builds the Live Show Conductor state for the current tick: resolves the active cue from
 * the (latency-corrected) deck position, applies the safe-mode gates (bridge/device/track/
 * package-match checks), and always produces a defined output frame — entering safe mode
 * with a safe fallback frame rather than ever leaving output undefined.
 * @param live - the current normalized live playback state (bridge/devices/decks/etc.).
 * @param approvedPackage - the approved package content matched to the active track, or null.
 * @param options - conductor tuning options (latency compensation, thresholds, safety toggles).
 * @returns the resolved conductor state, including its output frame, safety, and diagnostics.
 */
export function buildLiveConductorState(
  live: LivePlaybackState, approvedPackage: Pkg | null, options: LiveConductorOptions,
): DJSEMLiveConductorState {
  const now = live.updatedAt;
  const warnings: string[] = [];
  const deck = live.activeDeckId ? live.decks[live.activeDeckId] : undefined;
  const match = live.packageMatch;
  const matchConf = match?.confidence ?? 0;
  const fb = fallbackMode(approvedPackage);

  const diagnostics = {
    latencyMs: options.latencyCompensationMs, driftMs: live.liveClock.driftMs,
    packageMatchConfidence: matchConf, cueConfidence: 0, bridgeConnected: live.connection.bridgeConnected,
    lastEventType: live.recentEvents[0]?.type, lastEventAt: live.recentEvents[0]?.timestamp, warnings,
  };

  // ---- safe-mode gates (§29) ----
  let safeReason: string | null = null;
  if (!live.connection.bridgeConnected) safeReason = "bridge disconnected";
  else if (!live.connection.bridgeHealthy) safeReason = "bridge unhealthy / heartbeat timeout";
  else if (Object.keys(live.devices).length === 0) safeReason = "no devices";
  else if (!deck || deck.status === "lost") safeReason = "active deck lost";
  else if (!deck.track) safeReason = "no track loaded";
  else if (!match || match.status === "no_match") safeReason = "package match failed";
  // Matching hasn't run yet (the reducer's initial state right after TRACK_LOADED). Without
  // this explicit gate, a caller passing a stale/wrong-track `approvedPackage` alongside a
  // fresh `not_started` match would fall through every other gate here — the *rendered*
  // outputFrame still stays safe downstream (liveFrame only runs when `match?.packageId` is
  // set, which it never is for not_started), but this function's own top-level `mode`/
  // `safety.safeModeActive` fields would misreport "actively conducting" while holding a safe
  // frame, which any operator UI/diagnostics trusting those fields directly would get wrong.
  else if (match.status === "not_started") safeReason = "package matching not started";
  else if (match.status === "metadata_incomplete") safeReason = "track metadata incomplete — select package manually";
  else if (match.status === "manual_selection_required" || match.status === "multiple_candidates") safeReason = "multiple candidates — select package manually";
  else if (match.status === "unapproved_package") safeReason = "package not approved for live";
  else if (!approvedPackage) safeReason = "package content unavailable";

  if (safeReason) {
    return {
      mode: "safe_mode", activeDeckId: live.activeDeckId, activeTrackId: deck?.track?.liveTrackId,
      activePackageId: match?.packageId, packageMatch: match,
      outputFrame: safeFrame(now, fb, safeReason, match?.packageId),
      safety: { safeModeActive: true, reason: safeReason, fallbackVisualMode: fb }, diagnostics,
    };
  }

  // ---- conducting ----
  // Past the safe-mode gates above, deck is guaranteed non-null (the "!deck" gate returned).
  // Narrow once instead of scattering non-null assertions (TS can't see through the
  // safeReason-string gate pattern).
  const d = deck as NonNullable<typeof deck>;
  const cues = timeline(approvedPackage);
  let pos = d.playback.positionSeconds + options.latencyCompensationMs / 1000;
  const loop = d.loop;
  if (loop?.isLooping && loop.loopStartSeconds != null && loop.loopEndSeconds != null && loop.loopEndSeconds > loop.loopStartSeconds) {
    // constrain cue resolution to the loop range (§26). Half-open [start, end) to match
    // resolveActiveCue's own cue-window semantics below — pos landing exactly on the loop end
    // boundary previously escaped the clamp for one instant (condition was `>`, not `>=`).
    if (pos < loop.loopStartSeconds || pos >= loop.loopEndSeconds) pos = loop.loopStartSeconds;
  }
  const { cue, confidence } = resolveActiveCue(cues, pos);
  diagnostics.cueConfidence = confidence;

  let intensity = cue ? Number(cue.intensityBudget ?? cue.intensity ?? 0.5) : 0.3;
  const paused = !d.playback.isPlaying;
  if (paused) intensity = Math.min(intensity, 0.2);                    // §27 hold low on pause
  if (loop?.isLooping && !options.allowRepeatedDropOverdriveInLoops) intensity = Math.min(intensity, 0.6); // §26 tame loop overdrive
  if (options.reducedIntensityWhenDegraded && live.connection.connectionQuality === "degraded") intensity *= 0.6;
  if (confidence < 0.4) intensity = Math.min(intensity, 0.4);          // §24 reduce on low position confidence

  const mode: LiveConductorMode = paused ? "armed" : "conducting";
  const frame = cue && match?.packageId
    ? liveFrame(now, match.packageId, cue, intensity)
    : safeFrame(now, fb, "no active cue", match?.packageId);

  return {
    mode, activeDeckId: live.activeDeckId, activeTrackId: d.track?.liveTrackId,
    activePackageId: match?.packageId, activeCueId: cue ? String(cue.id ?? "") : undefined,
    trackPositionSeconds: d.playback.positionSeconds, correctedPositionSeconds: pos,
    beat: { beatNumber: d.playback.positionBeats, beatInBar: d.playback.beatInBar, barNumber: d.playback.barNumber, bpm: d.playback.bpm },
    packageMatch: match, outputFrame: frame,
    safety: { safeModeActive: false, fallbackVisualMode: fb }, diagnostics,
  };
}
